import React, { ReactNode, useRef, useState, useMemo, useCallback, useEffect } from "react";
import Select, { components, FormatOptionLabelMeta, Props as SelectProps, StylesConfig } from "react-select";
import CreatableSelect from "react-select/creatable";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";
import { OCFormControl } from "./FormControl";
import { alpha } from "@mui/material/styles";
import { Box, Checkbox, Grid, FormLabel } from "@mui/material";

export interface ISelectFieldProps extends Omit<SelectProps, "isOptionDisabled"> {
  name: string;
  value?: any;
  label?: string;
  labelSX?: any[];
  className?: string;
  multiMode?: string;
  maxMenuHeight?: number;
  hideSelectedValues?: boolean;
  placeholder?: string;
  options: any[];
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => string;
  isOptionDisabled?: (option: any, selectValue: any[]) => boolean;
  formatOptionLabel?: (option: any, formatOptionLabelMeta: FormatOptionLabelMeta<any>) => ReactNode;
  onChange?: (option: any) => any;
  meta?: Partial<FieldMetaProps<any>>;
  onCreateOption?: (inputValue: string) => void;
  creatable?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
}

const getMenuHeightLimit = (defaultMenuState: boolean, selectRef: any, forceCheck?: boolean): null | number => {
  const currentMaxMenuHeight = selectRef?.props?.maxMenuHeight;
  const currentMenuPlacement = selectRef?.props?.menuPlacement;
  const currentmenuIsOpen = forceCheck ?? selectRef?.props?.menuIsOpen;
  if (!defaultMenuState && currentmenuIsOpen) {
    const controlRef: any = selectRef?.controlRef;
    const currentSelectRect = controlRef?.getBoundingClientRect();
    const bodyRect = document.body.getBoundingClientRect();
    if (currentSelectRect && bodyRect) {
      const availableSpaceAbove = currentSelectRect.top;
      const availableSpaceBelow = bodyRect.height - currentSelectRect.bottom - 50;
      if (currentMenuPlacement === "bottom" && currentMaxMenuHeight > availableSpaceBelow) {
        return availableSpaceBelow;
      } else if (currentMenuPlacement === "top" && currentMaxMenuHeight > availableSpaceAbove) {
        return availableSpaceAbove;
      } else if (
        currentMenuPlacement === "auto" &&
        (currentMaxMenuHeight > availableSpaceBelow || currentMaxMenuHeight > availableSpaceAbove)
      ) {
        return Math.max(availableSpaceBelow, availableSpaceAbove);
      }
      return null;
    }
  }
  return null;
};

export const SelectField = (props: ISelectFieldProps): JSX.Element => {
  const {
    label,
    labelSX,
    name,
    value,
    onFocus,
    onBlur,
    onChange,
    meta,
    className,
    multiMode,
    disabled,
    isMulti,
    onCreateOption,
    creatable = true,
    maxMenuHeight,
    menuPortalTarget,
  } = props;
  const theme = useTheme();
  const selectMenuRef = useRef();
  const currentSelect = selectMenuRef?.current ?? null;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const defaultMenuState = props?.menuIsOpen ?? false;
  const selectRef = useMemo(() => (currentSelect as any)?.select, [currentSelect]);
  const [limitMenuHeight, setLimitMenuHeight] = useState<number>(null);

  const multiModeProps = useMemo(() => {
    return multiMode === "checkbox" ? { hideSelectedValues: true, hideSelectedOptions: false } : {};
  }, [multiMode]);

  const alwaysShowPlaceholder = useCallback((props: any): JSX.Element => {
    const placeholder = props?.selectProps?.placeholder ?? "";
    if (!!props.children && props.children[0].length > 0) {
      return (
        <Box sx={{ flexGrow: 2, pl: 2 }}>
          {props.children[0].length} item{props.children[0].length === 1 ? "" : "s"} selected
        </Box>
      );
    }
    return <Box sx={{ flexGrow: 2, pl: 2 }}>{placeholder}</Box>;
  }, []);

  const newComponents = useMemo(() => {
    const checkboxMode = (props: any): JSX.Element => {
      return (
        <components.Option {...props}>
          <Checkbox checked={props.isSelected} sx={{ p: 0, m: 0, pr: 2 }} />
          {props.label}
        </components.Option>
      );
    };
    return multiMode === "checkbox" ? { Option: checkboxMode, ValueContainer: alwaysShowPlaceholder } : null;
  }, [multiMode, alwaysShowPlaceholder]);

  const hasError = meta?.error ? true : false;
  const customStyles: StylesConfig = {
    container: (styles) => ({
      ...styles,
      position: "relative",
      borderRadius: 4,
      border: `1px solid ${theme.custom.forms.inputBorder}`,
      ":active": {
        ...styles[":active"],
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        border: `1px solid ${theme.palette.primary.main}`,
      },
      ":focus": {
        ...styles[":focus"],
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        border: `1px solid ${theme.palette.primary.main}`,
      },
      ":hover": {
        ...styles[":hover"],
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        border: `1px solid ${theme.palette.primary.main}`,
      },
      "&.error": {
        transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        borderColor: `${theme.palette.error.dark} !important`,
      },
    }),
    control: (styles) => ({
      ...styles,
      backgroundColor: hasError ? alpha(theme.palette.error.dark, 0.25) : theme.custom.forms.inputBack,
      boxShadow: hasError ? `${alpha(theme.palette.error.dark, 0.25)} 0 0 0 0.2rem !important` : "",
      border: "none",
      fontSize: 11,
      padding: 0,
      margin: 0,
      minHeight: 26,
    }),
    menuList: (styles) => ({
      ...styles,
      maxHeight: "190px",
      backgroundColor: theme.custom.forms.inputBack,
      border: `1px solid ${theme.custom.forms.inputBorder}`,
      boxShadow: `0px 0px 10px ${theme.palette.common.black}`,
    }),
    menu: (styles) => ({
      ...styles,
      color: theme.custom.forms.pristineColor,
      maxHeight: "190px",
      padding: 0,
      margin: 0,
      position: "absolute",
      zIndex: 50000,
    }),
    menuPortal: (styles) => ({
      ...styles,
      position: "absolute",
      zIndex: 50000,
    }),
    indicatorSeparator: (styles) => ({
      color: theme.custom.forms.pristineColor,
      padding: "0px 2px",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      alignSelf: "end",
      cursor: "pointer",
      padding: "0px",
      marginBottom: "3px",
      border: `1px solid ${theme.palette.grey[800]}`,
      borderRadius: 3,
      color: theme.custom.forms.pristineColor,
      ":active": {
        ...styles[":active"],
        color: theme.custom.forms.pristineColor,
      },
      ":focus": {
        ...styles[":focus"],
        color: theme.custom.forms.pristineColor,
      },
      ":hover": {
        ...styles[":hover"],
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
      },
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      cursor: "pointer",
      padding: "0px 8px 4px 8px",
      alignSelf: "end",
      color: theme.custom.forms.pristineColor,
      ":active": {
        ...styles[":active"],
        color: theme.custom.forms.pristineColor,
      },
      ":focus": {
        ...styles[":focus"],
        color: theme.custom.forms.pristineColor,
      },
      ":hover": {
        ...styles[":hover"],
        color: theme.custom.forms.pristineColor,
      },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        fontSize: 11,
        backgroundColor: isSelected ? theme.palette.primary.dark : theme.custom.forms.inputBack,
        color: isSelected ? theme.palette.common.white : theme.custom.forms.inputText,
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...styles[":active"],
          backgroundColor: theme.custom.forms.pristineColor,
        },
        ":hover": {
          ...styles[":hover"],
          backgroundColor: theme.palette.info.main,
          color: theme.palette.common.white,
          cursor: "pointer",
        },
      };
    },
    input: (styles) => ({
      ...styles,
      //Do not set bgcolor here, it will hide the value in the single value placeholder.
      fontSize: 11,
      color: theme.palette.common.white,
      padding: 0,
      margin: 0,
      cursor: "text",
    }),
    groupHeading: (styles) => ({
      ...styles,
      paddingTop: 3,
      paddingBottom: 3,
      marginTop: 2,
      backgroundColor: theme.palette.common.black,
    }),
    placeholder: (styles) => ({
      ...styles,
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      width: "100%",
      color: theme.palette.common.white,
    }),
    singleValue: (styles) => ({
      ...styles,
      color: theme.palette.common.white,
    }),
    multiValue: (styles) => ({
      ...styles,
      color: theme.palette.common.white,
      backgroundColor: theme.palette.info.dark,
      borderRadius: "2px",
      border: `1px solid ${theme.palette.info.main}`,
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: theme.palette.common.white,
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: theme.palette.common.white,
      cursor: "pointer",
    }),
  };

  const handleOnChange = (outputValue: any): void => {
    if (onChange && typeof onChange === "function") {
      onChange(outputValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLElement>): void => {
    setIsFocused(true);
    if (onFocus && typeof onFocus === "function") {
      onFocus(e as React.FocusEvent<HTMLInputElement>);
    }
  };

  const handleBlur = (e: React.FocusEvent<Element>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as React.FocusEvent<HTMLInputElement>);
      }
    }, 1);
  };

  const internalValue = value ? value : !value && isMulti ? [] : null;
  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        {label && (
          <Grid
            item
            sx={{
              display: "flex",
              justifyContent: hasError ? "space-between" : "flex-end",
              textAlign: "right",
            }}
            xs
          >
            <FieldError meta={meta} icon={true} />
            <FormLabel sx={{ ...labelSX }}>{label}</FormLabel>
          </Grid>
        )}
        <Grid item xs={label ? 7 : 12}>
          {onCreateOption && creatable ? (
            //@ts-ignore - Added to resolve a conflict in the type passed in for isOptionDisabled
            <CreatableSelect
              {...props}
              id={name + "_selection"}
              inputId={name}
              ref={selectMenuRef}
              className={`mui5react-select-container ${hasError ? "error" : ""}}`}
              classNamePrefix="mui5react-select"
              styles={customStyles}
              menuPlacement="auto"
              menuPortalTarget={menuPortalTarget}
              menuShouldBlockScroll={false}
              menuShouldScrollIntoView={true}
              closeMenuOnScroll={true}
              closeMenuOnSelect={!!multiMode || isMulti ? false : true}
              value={internalValue}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onChange={onChange}
              onCreateOption={onCreateOption}
              onMenuOpen={() => {
                if (selectRef) {
                  const newLimit = getMenuHeightLimit(defaultMenuState, selectRef, true);
                  setLimitMenuHeight(newLimit);
                }
              }}
              {...multiModeProps}
            />
          ) : (
            //@ts-ignore - Added to resolve a conflict in the type passed in for isOptionDisabled
            <Select
              {...props}
              id={name + "_selection"}
              inputId={name}
              ref={selectMenuRef}
              className={`mui5react-select-container ${hasError ? "error" : ""}`}
              classNamePrefix="mui5react-select"
              styles={customStyles}
              menuPlacement={"auto"}
              menuPortalTarget={menuPortalTarget || document.body}
              menuShouldBlockScroll={false}
              menuShouldScrollIntoView={true}
              closeMenuOnScroll={true}
              closeMenuOnSelect={!!multiMode || isMulti ? false : true}
              value={internalValue}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onChange={handleOnChange}
              components={{ ...components, ...newComponents }}
              onMenuOpen={() => {
                if (selectRef) {
                  const newLimit = getMenuHeightLimit(defaultMenuState, selectRef, true);
                  setLimitMenuHeight(newLimit);
                }
              }}
              {...multiModeProps}
            />
          )}
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
